import React, { useState, useEffect, useContext } from 'react'
import { Box, Typography, Button, IconButton, Paper, TextField, Modal, CircularProgress } from '@mui/material'

import '../../../styles/css/chat.css'
import UserIcon from '../../../assets/user-placeholder.svg'
import {
    CommentProfilepic,
    CommentBody,
    CommentMenu
} from '../../../styles/js/Chat.style'
import { CommentData } from '../../../utils/types'
import { get, post, remove } from '../../../utils/http'
import { Cancel, DeleteForever, Edit, Refresh, Save } from '@mui/icons-material'
import { lightGrey, msgBoxColor } from '../../../styles/js/colors.style'
import { modalStyle } from '../../../styles/js/common'
import { ViewerContext } from './context'

interface ChatProps {
    imageId: number
}

const ChatComponent = ({ imageId }: ChatProps) => {
    const { readonly } = useContext(ViewerContext)

    const [comments, setComments] = useState<CommentData[]>([])
    const [commentInfo, setCommentInfo] = useState<CommentData | null>(null)
    const [editComment, setEditComment] = useState<string>('')
    const [enteredComment, setEnteredComment] = useState<string>('')
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [apiWait, setApiWait] = useState<boolean>(false)
    const [openConfirmBox, setOpenConfirmBox] = useState<boolean>(false)

    const userRole = localStorage.getItem('role') || ''
    const fullName = localStorage.getItem('fullName') || 'user'
    const isAdmin = ['dev', 'admin', 'superadmin'].includes(userRole)
    const adminId = Number(localStorage.getItem('adminId') || '')
    const userId = Number(localStorage.getItem('user_id') || '')

    const scrollToBottom = () => {
        const box = document.querySelector('#text-chat-box')
        if (box) box.scrollTo(0, box.scrollHeight);
    }

    const getCommentData = async () => {
        setIsLoading(true)
        try {
            const res = await get(`Comments/imageid/${imageId}`)
            setComments(res)
        } catch (err) {
            console.log(err)
        } finally {
            scrollToBottom()
            setIsLoading(false)
        }
    }

    const postCommentData = async () => {
        setApiWait(true)
        try {
            const commentData = {
                userid: isAdmin ? adminId : userId,
                username: fullName,
                imageid: imageId,
                comment: enteredComment
            }
            await post(`Comments`, commentData)
            getCommentData()
            setEnteredComment('')
        } catch (err) {
            console.log(err)
        } finally {
            scrollToBottom()
            setApiWait(false)
        }
    }

    const saveCommentHandler = async () => {
        try {
            await post(`Comments/${commentInfo!.id}`, {comment: editComment})
            getCommentData()
            setCommentInfo(null)
        } catch (err) {
            console.log(err)
        }
    }

    const deleteComment = async () => {
        setApiWait(true)
        try {
            await remove(`Comments/${commentInfo!.id}`)
            getCommentData()
        } catch (err) {
            console.log(err)
        } finally {
            setCommentInfo(null)
            setApiWait(false)
            setOpenConfirmBox(false)
            scrollToBottom()
        }
    }

    useEffect(() => {
        getCommentData()
    }, [imageId])   // eslint-disable-line

    const commentEditHandler = async (props: CommentData) => {
        setEditComment(props.text)
        setCommentInfo(props)
    }

    const deleteCommentHandler = (props: CommentData) => {
        setCommentInfo(props)
        setOpenConfirmBox(true)
    }

    const cancelDeleteComment = () => {
        setCommentInfo(null)
        setOpenConfirmBox(false)
    }

    const commentChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => setEnteredComment(e.target.value)

    const handleKeyChange = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter') postCommentData()
    }

    const editCommentChangeHandler = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEditComment(e.target.value)

    const handleEditKeyChange = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'Enter') saveCommentHandler()
    }

    const cancelCommentEditHandler = () => {
        setEditComment('')
        setCommentInfo(null)
    }

    const isSameDay = (date1: Date, date2: Date) => {
        return (
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate()
        )
    }

    return (
        <Box className="d-flex flex-column align-items-center w-100" sx={{maxWidth: 1200, margin: '0 auto'}}>
            <Paper elevation={3} className='w-100'>
                <Box className="p-3">
                    <Box
                        id='text-chat-box'
                        className='d-flex flex-column gap-3 p-3'
                        sx={{ backgroundColor: lightGrey, overflowY: 'auto', maxHeight: 'calc(100vh - 320px)' }}
                    >
                        {comments.map((i, index) => {
                            const date = new Date(i.created)
                            const datePrev = index > 0 ? new Date(comments[index -1].created) : date
                            const fullDate = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
                            const selfComment = isAdmin ? i.user_id === adminId : i.user_id === userId
                            const showDate = index > 0 ? !isSameDay(datePrev, date) : true
                            return (
                                <React.Fragment key={i.id}>
                                {showDate && <Typography variant="caption" sx={{color: 'grey', alignSelf: 'center'}}>{fullDate}</Typography>}
                                <Box className="d-flex gap-2" sx={{alignSelf: `${selfComment ? 'end' : 'start'}`}}>
                                    {!selfComment && <CommentProfilepic><img src={UserIcon} alt="user icon"/></CommentProfilepic>}
                                    <Box className="d-flex flex-column gap-1">
                                        {!selfComment && <Typography variant="body2">{i.user_name}</Typography>}
                                        <CommentBody className={selfComment ? 'self-comment': 'user-comment'}>
                                            {commentInfo?.id === i.id && editComment.length
                                                ? <TextField
                                                    sx={{backgroundColor: msgBoxColor}}
                                                    value={editComment}
                                                    onChange={editCommentChangeHandler}
                                                    onKeyUp={handleEditKeyChange}
                                                    fullWidth
                                                >
                                                    {i.text}
                                                </TextField>
                                                : <Typography className='py-2 px-3'>{i.text}</Typography>
                                            }
                                            <CommentMenu>
                                                {!readonly && selfComment
                                                    ? editComment.length
                                                        ? <>
                                                            <IconButton size="small" onClick={cancelCommentEditHandler}><Cancel /></IconButton>
                                                            <IconButton size="small" onClick={saveCommentHandler}><Save /></IconButton>
                                                        </>
                                                        : <>
                                                            <IconButton size="small" onClick={() => deleteCommentHandler(i)}><DeleteForever /></IconButton>
                                                            <IconButton size="small" onClick={() => commentEditHandler(i)}><Edit /></IconButton>
                                                        </>
                                                    : null
                                                }
                                            </CommentMenu>
                                        </CommentBody>
                                    </Box>
                                </Box>
                                </React.Fragment>
                            )
                        })}
                    </Box>
                    <Box className="d-flex flex-column gap-3">
                        {!readonly
                            ? <TextField
                                onChange={commentChangeHandler}
                                onKeyUp={handleKeyChange}
                                value={enteredComment}
                                label="Text"
                                variant="outlined"
                                autoComplete="off"
                                fullWidth
                                disabled={apiWait}
                            />
                            : null
                        }
                        <Box className='d-flex justify-content-between align-items-center gap-3 pt-2'>
                            <IconButton onClick={getCommentData} disabled={isLoading || apiWait}  aria-label="reload xml list">
                                <Refresh />
                            </IconButton>
                            {!readonly
                                ? <Button
                                    disabled={apiWait}
                                    onClick={postCommentData}
                                    variant="contained"
                                >
                                    Comment
                                </Button>
                                : null
                            }
                        </Box>
                    </Box>
                </Box>
            </Paper>
            <Modal open={openConfirmBox} onClose={cancelDeleteComment}>
                <Box sx={modalStyle}>
                    <Box className='d-flex flex-column gap-4' sx={{maxWidth: 500}}>
                        <Typography variant="h5" component="h1">Delete selected comment?</Typography>
                        <Box className='d-flex gap-3 justify-content-center'>
                            {apiWait
                                ? <CircularProgress size={24}/>
                                : <>
                                    <Button variant="outlined" onClick={cancelDeleteComment}>Cancel</Button>
                                    <Button color="error" variant="contained" onClick={deleteComment}>Delete</Button>
                                </>
                            }
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </Box>
    )
}

export default ChatComponent